import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';

interface Footer_v2Props {
  region: 'ru' | 'us';
}

export default function Footer_v2({ region }: Footer_v2Props) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isEnglishPage = location.pathname.startsWith('/en') || region === 'us';
  const isRussianPage = region === 'ru';

  // Region'a göre dili ayarla - ÖNCE
  useEffect(() => {
    const targetLang = region === 'ru' ? 'ru' : 'us';
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [region, i18n]);

  // Dil değişikliğini zorla
  if (region === 'ru' && i18n.language !== 'ru') {
    i18n.changeLanguage('ru');
  } else if (region === 'us' && i18n.language !== 'us') {
    i18n.changeLanguage('us');
  }

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.REACT_APP_NAVIGATE(`/${region}`);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const handleFairsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.REACT_APP_NAVIGATE(`/${region}/fairs`);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
  };

  const showFairs = region !== 'ru';

  const handleCuttingThicknessClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.REACT_APP_NAVIGATE(`/${region}/cutting-thickness`);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
  };

  // Region-specific contact information
  const contactInfo = region === 'ru' ? {
    companyName: 'ООО «АЖАН»',
    phone: '+7 (495) 777-88-17',
    phoneHref: 'tel:+74957778817',
    fax: '+7 (495) 777-88-16',
    email: 'info@ajan.ru',
    address: 'Россия, Москва, Смольная 24А (БЦ «Муравей»), офис 706, 7-ой этаж',
    addressLink: 'https://yandex.com.tr/maps/213/moscow/?from=mapframe&ll=37.477440%2C55.859760&mode=usermaps&source=mapframe&um=constructor%3AktEDAx_dKkZVS9YaD-u6i10085vrqs5a&utm_source=mapframe&z=15'
  } : {
    companyName: 'AJAN USA LLC',
    phone: '470-970-2526',
    phoneHref: 'tel:+14709702526',
    fax: '',
    email: 'support@ajanusa.com',
    address: '2513 Shallowford Road Bldg 200, Suite 211, Marietta, Georgia 30066',
    addressLink: 'https://www.google.com/maps?ll=34.04195,-84.478334&z=15&t=m&hl=tr&gl=TR&mapclient=embed&cid=9062301240525143447'
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Logo ve Açıklama */}
          <div className="md:col-span-1">
            <img 
              src="https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/5cdb8acd1a926456732ace3d319bea51.png"
              alt="AJAN LASER"
              className="h-12 mb-4"
            />
            <p className="text-gray-300 text-base leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-[22px]">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href={`/${region}`}
                  onClick={handleHomeClick}
                  className="text-lg hover:text-[#D62828] transition-colors cursor-pointer"
                >
                  {t('footer.home')}
                </a>
              </li>
              {showFairs && (
                <li>
                  <a 
                    href={`/${region}/fairs`}
                    onClick={handleFairsClick}
                    className="text-lg hover:text-[#D62828] transition-colors cursor-pointer"
                  >
                    {t('footer.fairs')}
                  </a>
                </li>
              )}
              <li>
                <a 
                  href={`/${region}/cutting-thickness`}
                  onClick={handleCuttingThicknessClick}
                  className="text-lg hover:text-[#D62828] transition-colors cursor-pointer"
                >
                  {t('footer.cuttingThickness')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-[22px]">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <i className="ri-phone-line text-[#D62828] mt-1"></i>
                <div>
                  <div className="text-lg">
                    <span className="text-gray-400">{t('footer.phone')} </span>
                    <a href={contactInfo.phoneHref} className="hover:text-[#D62828] transition-colors">
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
              </li>
              {contactInfo.fax && (
                <li className="flex items-start gap-2">
                  <i className="ri-printer-line text-[#D62828] mt-1"></i>
                  <div>
                    <div className="text-lg">
                      <span className="text-gray-400">{t('footer.fax')} </span>
                      <span>{contactInfo.fax}</span>
                    </div>
                  </div>
                </li>
              )}
              <li className="flex items-start gap-2">
                <i className="ri-mail-line text-[#D62828] mt-1"></i>
                <div>
                  <div className="text-lg">
                    <span className="text-gray-400">{t('footer.email')} </span>
                    <a href={`mailto:${contactInfo.email}`} className="hover:text-[#D62828] transition-colors">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-map-pin-line text-[#D62828] mt-1"></i>
                <div>
                  <div className="text-lg">
                    <span className="text-gray-400">{t('footer.address')} </span>
                    <p className="font-medium text-white inline">{contactInfo.companyName}</p>
                  </div>
                  <a 
                    href={contactInfo.addressLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg hover:text-[#D62828] transition-colors cursor-pointer block mt-1"
                  >
                    {contactInfo.address}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-[22px]">{t('footer.certifications')}</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <i className="ri-award-line text-[#D62828]"></i>
                <span className="text-lg">{t('footer.ceCertificate')}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-[#D62828]"></i>
                <span className="text-lg">{t('footer.iso9001')}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-flashlight-line text-[#D62828]"></i>
                <span className="text-lg">{t('footer.emcLvdTests')}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-plug-line text-[#D62828]"></i>
                <span className="text-lg">{t('footer.tsEnIso12100')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-lg text-gray-400">
              {t('footer.rights')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
